import React from 'react';
import * as consts from 'config/consts';
import { GetUserBrokerResultItem } from 'config/api';
import { useRouting } from 'hooks/useRouting';
import { useScroll } from 'hooks/useScroll';
import {
  addSignCommaFormat,
  commaFormat,
} from 'components/features/new-report/utils/formatter';
import {
  amountToAmountColor,
  isSymbolEmpty,
} from 'components/features/utils/util';
import {
  AssetTypeKey,
  getQuantityUnit,
  isDomestic,
  isForeign,
  isQtyHiddenAssetType,
  isNotExistDetailAssetTypes,
  toAssetTypeKey,
} from 'components/features/utils/assetType';
import {
  AssetBundleType,
  isOutOfDateBroker,
} from 'components/features/home/Home';
import { PositionItemsProps } from 'components/features/home/ProfitAndLossStuffing/ProfitAndLossStuffing';
import { WithEmptyString } from 'components/features/home/WithEmptyView';
import { goToGrouping } from 'components/features/home/PortfolioRatioStuffing/PortfolioRatioStuffing';
import { Side } from 'components/features/utils/side';
import { isForeignStockTypes } from 'components/features/utils/productTypes';
import { brokerIDToName } from 'components/features/utils/converter';
import {
  AssetOrganizedPortfolioItemForeignProps,
  AssetOrganizedPortfolioItemProps,
  SimpleOrganized,
  isHiddenProductTypes,
} from './PortfolioStuffingAsset';
import { AssetOrganizedPortfolio } from './AssetOrganizedPortfolio';
import { AssetOrganizedPortfolioForeign } from './AssetOrganizedPortfolioForeign';

const simple: SimpleOrganized = { simple: true };

const mnkbBrokerIdToOrd = (brokerId: string) => {
  switch (brokerId) {
    case '1100':
      return 1;
    case 'wealthnavi':
      return 2;
    case 'theo':
      return 3;
  }
  return 4;
};

const assetTypeKeyToDisp = (key: AssetTypeKey): number => {
  switch (key) {
    case consts.AssetType['国内現物']:
      return 1;
    case consts.AssetType['未上場株式']:
      return 2;
    case consts.AssetType['投資信託']:
      return 3;
    case consts.AssetType['外国株式']:
      return 4;
    case consts.AssetType['国内ETF・ETN']:
      return 5;
    case consts.AssetType['外国ETF・ETN']:
      return 6;
    case consts.AssetType['国内REIT']:
      return 7;
    case consts.AssetType['外国REIT']:
      return 8;
    case consts.AssetType['国内債券']:
      return 9;
    case consts.AssetType['外国債券']:
      return 10;
    case consts.AssetType['国内EB']:
      return 11;
    case consts.AssetType['外国EB']:
      return 12;
    case consts.AssetType['不動産']:
      return 13;
    case consts.AssetType['ロボアドバイザー']:
      return 14;
    case consts.AssetType['信用']:
      return 15;
    case consts.AssetType['預金_MRF']:
      return 16;
    case consts.AssetType['その他']:
    default:
      return 17;
  }
};

type DomesticPortfolioItemProps = AssetOrganizedPortfolioItemProps;

type ForeignPortfolioItemProps = Omit<
  AssetOrganizedPortfolioItemForeignProps,
  'currencyMode'
>;

type DomesticPortfolioProps = {
  assetTypeKey: AssetTypeKey;
  items: DomesticPortfolioItemProps[];
};

type ForeignPortfolioProps = {
  assetTypeKey: AssetTypeKey;
  items: ForeignPortfolioItemProps[];
};

type PortfolioStuffingItemProps =
  | DomesticPortfolioItemProps
  | ForeignPortfolioItemProps;

type PortfolioStuffingProps = DomesticPortfolioProps | ForeignPortfolioProps;

const getAssetOrganizedPortfolioItemProps = (
  positions: PositionItemsProps[],
  brokers: GetUserBrokerResultItem[],
  other: number,
  getIsOutOfDate: (item: { account: string }) => boolean | null
): PortfolioStuffingItemProps[] => {
  // memo: positionsのデータ加工
  //       預金・MRFとその他以外のデータを作成
  const availableBrokerArr = brokers
    .filter(item => item.Available)
    .map(item => item.Account);

  const availablePositions = positions.filter(item =>
    availableBrokerArr.includes(item.account)
  );

  const positionsAssetData = availablePositions
    .filter(item => !isHiddenProductTypes(item.productType))
    .sort(
      (a, b) => mnkbBrokerIdToOrd(a.brokerID) - mnkbBrokerIdToOrd(b.brokerID)
    )
    .sort((a, b) => toAssetTypeKey(a) - toAssetTypeKey(b))
    .map(
      (x): PositionItemsProps =>
        toAssetTypeKey(x) === consts.AssetType['ロボアドバイザー'] &&
        brokerIDToName(x.brokerID)
          ? { ...x, symbolName: brokerIDToName(x.brokerID) ?? '' }
          : x
    )
    .reduce((acc, v): PositionItemsProps[] => {
      const last = acc[acc.length - 1];
      if (
        last &&
        (last.brokerID === consts.ManualBrokerID.手入力口座 ||
          v.brokerID === consts.ManualBrokerID.手入力口座)
      )
        return [...acc, v];

      const isSameRoboAdvisor =
        last &&
        last.brokerID &&
        !isForeignStockTypes(last.productType) &&
        !isForeignStockTypes(v.productType) &&
        brokerIDToName(last.brokerID) !== null &&
        brokerIDToName(last.brokerID) === brokerIDToName(v.brokerID);

      if (isSameRoboAdvisor)
        return [
          ...(acc.slice(0, acc.length - 1) as PositionItemsProps[]),
          {
            ...last,
            currentAmount: (last.currentAmount ?? 0) + (v.currentAmount ?? 0),
            price: (last.price ?? 0) + (v.price ?? 0),
            profitOrLoss: (last.profitOrLoss ?? 0) + (v.profitOrLoss ?? 0),
            qty: (last.qty ?? 0) + (v.qty ?? 0),
            symbolName: brokerIDToName(last.brokerID) ?? '',
          },
        ];

      const isSameSymbol =
        last &&
        last.brokerID &&
        last.productType === v.productType &&
        last.cashMargin === v.cashMargin &&
        last.symbol === v.symbol &&
        last.symbolName === v.symbolName;

      if (isSameSymbol)
        return [
          ...(acc.slice(0, acc.length - 1) as PositionItemsProps[]),
          {
            ...last,
            currentAmount: (last.currentAmount ?? 0) + (v.currentAmount ?? 0),
            profitOrLoss: (last.profitOrLoss ?? 0) + (v.profitOrLoss ?? 0),
            qty: (last.qty ?? 0) + (v.qty ?? 0),
          },
        ];
      return [...acc, v];
    }, [] as PositionItemsProps[])
    .map(
      (state): PortfolioStuffingItemProps => {
        const assetTypeKey = toAssetTypeKey(state);
        const isQtyHidden = isQtyHiddenAssetType(assetTypeKey);
        const isManual = state.brokerID === consts.ManualBrokerID.手入力口座;

        // MEMO: 外国系
        if (isForeign(assetTypeKey)) {
          const ret: ForeignPortfolioItemProps = {
            assetTypeKey,
            brokerID: state.brokerID,
            amount: {
              JPY:
                state.currentAmount === null
                  ? '---'
                  : commaFormat(state.currentAmount),
              FOREIGN:
                state.currentAmountUSD === null
                  ? '---'
                  : commaFormat(state.currentAmountUSD, {
                      numberOfDecimals: 2,
                    }),
            },
            identifier: isManual ? '直接入力' : state.symbol,
            link: null,
            name: state.symbolName,
            profitOrLoss: isManual
              ? simple
              : {
                  JPY: addSignCommaFormat(state.profitOrLoss),
                  FOREIGN: addSignCommaFormat(state.profitOrLossUSD, {
                    numberOfDecimals: 2,
                  }),
                },
            profitOrLossColor: isManual
              ? simple
              : {
                  JPY: amountToAmountColor(state.profitOrLoss ?? 0),
                  FOREIGN: amountToAmountColor(state.profitOrLossUSD ?? 0),
                },
            quantity: isQtyHidden ? simple : state.qty,
            quantityUnit: isQtyHidden ? simple : getQuantityUnit(assetTypeKey),
            refresh: getIsOutOfDate(state)
              ? {
                  isOutOfDate: true,
                  account: state.account,
                }
              : null,
            side: state.side as Side,
            unit: state.currencyUnit ?? '',
          };
          return ret;
        }

        // MEMO: 国内系
        const isOutOfDate = getIsOutOfDate(state);

        const getLink = (state: PositionItemsProps): string | null => {
          if (isManual) return null;
          if (state.symbol === null || state.symbol === '') return null;
          if (
            isSymbolEmpty(state.symbol) ||
            isNotExistDetailAssetTypes(assetTypeKey)
          )
            return null;

          if (brokerIDToName(state.brokerID))
            return `/accountdetail/${state.account}`;

          if (assetTypeKey === consts.AssetType['投資信託']) {
            // MEMO: 『類推中』はMONO-***、『類推完了』はDC-***で返却
            const isDataCooperation =
              state.symbol !== null && /^MONO/.test(state.symbol);
            const isAnalogyComplete =
              state.symbol !== null &&
              (state.symbol === '' || /^DC/.test(state.symbol));

            if (isDataCooperation) {
              return `/accountdetail/${state.account}`;
            } else if (!isAnalogyComplete) {
              return `/tradedetail/${state.symbol}`;
            } else {
              return null;
            }
          }

          return `/tradedetail/${state.symbol}`;
        };

        const ret = {
          assetTypeKey: assetTypeKey as AssetTypeKey,
          brokerID: state.brokerID,
          quantity: isQtyHidden ? simple : state.qty,
          side:
            assetTypeKey === consts.AssetType['信用']
              ? (state.side as Side)
              : null,
          amount:
            state.currentAmount === null
              ? '---'
              : commaFormat(state.currentAmount, {
                  numberOfDecimals: 0,
                }),
          profitOrLoss: isManual
            ? simple
            : addSignCommaFormat(state.profitOrLoss, {
                numberOfDecimals: 0,
              }),
          profitOrLossColor: isManual
            ? simple
            : amountToAmountColor(state.profitOrLoss ?? 0),
          identifier: isManual ? '直接入力' : state.symbol,
          quantityUnit: isQtyHidden ? simple : getQuantityUnit(assetTypeKey),
          name: state.symbolName,
          link: getLink(state),
          refresh: isOutOfDate
            ? {
                isOutOfDate,
                account: state.account,
              }
            : null,
        };
        return ret;
      }
    );

  // memo: brokersのデータ加工
  //       預金・MRFのデータを作成
  const brokersAssetData = brokers
    .filter(cash => {
      return (
        cash.Available &&
        // memo: 手入力口座は預金以外削除
        !(
          cash.AggregateSource === consts.AggregateSourceTypes.手入力 &&
          cash.CashAmount === 0
        )
      );
    })
    .map(cash => {
      const ret: AssetOrganizedPortfolioItemProps = {
        assetTypeKey: consts.AssetType['預金_MRF'],
        brokerID:
          cash.AggregateSource === consts.AggregateSourceTypes.手入力
            ? consts.ManualBrokerID.手入力口座
            : simple,
        amount: commaFormat(cash.CashAmount),
        identifier:
          cash.AggregateSource === consts.AggregateSourceTypes.手入力
            ? '直接入力'
            : simple,
        name: cash.BrokerName,
        link: null,
        profitOrLoss: simple,
        profitOrLossColor: simple,
        quantity: simple,
        quantityUnit: simple,
        side: simple,
        refresh: isOutOfDateBroker({ status: cash.Status })
          ? { isOutOfDate: true, account: cash.Account }
          : null,
      };
      return ret;
    });

  // memo: otherのデータ加工
  //       その他のデータを作成
  const otherAssetData = {
    assetTypeKey: consts.AssetType['その他'],
    brokerID: simple,
    amount: commaFormat(other),
    side: simple,
    profitOrLoss: simple,
    profitOrLossColor: simple,
    identifier: simple,
    name: simple,
    quantity: simple,
    quantityUnit: simple,
    link: simple,
    refresh: null,
  };

  return [...positionsAssetData, ...brokersAssetData, otherAssetData];
};

export const PortfolioStuffingAssetList = ({
  assetBundleType,
  title,
  positions,
  brokersInfo,
  otherAmount,
  getIsOutOfDate,
}: {
  assetBundleType: AssetBundleType;
  title: string;
  positions: PositionItemsProps[];
  brokersInfo: GetUserBrokerResultItem[];
  otherAmount: number;
  getIsOutOfDate: (arg: { account: string }) => boolean | null;
}) => {
  const { pushRouting } = useRouting();

  // memo: 各APIで取得したデータを一つにまとめる
  const items = getAssetOrganizedPortfolioItemProps(
    positions,
    brokersInfo,
    otherAmount,
    getIsOutOfDate
  );

  const organizedItems_ = items
    .filter(
      item =>
        !(
          (item.assetTypeKey === consts.AssetType['預金_MRF'] ||
            item.assetTypeKey === consts.AssetType['その他']) &&
          item.amount === '0'
        )
    )
    .sort(
      (a, b) =>
        assetTypeKeyToDisp(a.assetTypeKey) - assetTypeKeyToDisp(b.assetTypeKey)
    );

  const organizedItems = organizedItems_.reduce(
    (
      acc,
      current
    ): {
      init: PortfolioStuffingProps[];
      last: PortfolioStuffingProps | null;
    } => {
      if (!acc.last) {
        const last = isDomestic(current.assetTypeKey)
          ? {
              assetTypeKey: current.assetTypeKey,
              items: [current] as DomesticPortfolioItemProps[],
            }
          : {
              assetTypeKey: current.assetTypeKey,
              items: [current] as ForeignPortfolioItemProps[],
            };

        return {
          init: acc.init,
          last: last as PortfolioStuffingProps,
        };
      }

      if (acc.last.assetTypeKey === current.assetTypeKey) {
        const last = isDomestic(current.assetTypeKey)
          ? {
              assetTypeKey: acc.last.assetTypeKey,
              items: [
                ...acc.last.items,
                current,
              ] as DomesticPortfolioItemProps[],
            }
          : {
              assetTypeKey: acc.last.assetTypeKey,
              items: [
                ...acc.last.items,
                current,
              ] as ForeignPortfolioItemProps[],
            };

        return {
          init: acc.init,
          last: last as PortfolioStuffingProps,
        };
      } else {
        const last = isDomestic(current.assetTypeKey)
          ? {
              assetTypeKey: current.assetTypeKey,
              items: [current] as DomesticPortfolioItemProps[],
            }
          : {
              assetTypeKey: current.assetTypeKey,
              items: [current] as ForeignPortfolioItemProps[],
            };

        return {
          init: [...acc.init, acc.last],
          last: last as PortfolioStuffingProps,
        };
      }
    },
    {
      init: [] as PortfolioStuffingProps[],
      last: null as PortfolioStuffingProps | null,
    }
  );

  const organizedList =
    organizedItems.last === null
      ? organizedItems.init
      : [...organizedItems.init, organizedItems.last];

  useScroll();

  return (
    <div className="p-4 pb-0 max-w-md mx-auto">
      <div className="text-lg font-bold mb-4">{title}</div>
      <WithEmptyString
        View={(): React.ReactElement => (
          <>
            {organizedList.map((list, i) => {
              return isDomestic(list.assetTypeKey) ? (
                <AssetOrganizedPortfolio
                  {...{
                    assetTypeKey: list.assetTypeKey,
                    items: list.items as Omit<
                      AssetOrganizedPortfolioItemProps,
                      'assetTypeKey'
                    >[],
                  }}
                />
              ) : (
                <AssetOrganizedPortfolioForeign
                  {...{
                    assetTypeKey: list.assetTypeKey,
                    items: list.items as Omit<
                      AssetOrganizedPortfolioItemForeignProps,
                      'assetTypeKey' | 'currencyMode'
                    >[],
                  }}
                />
              );
            })}
          </>
        )}
        empty={{
          message: '表示可能な資産がありません',
        }}
        isEmpty={organizedList.length === 0}
      />
      {assetBundleType.type === 'グループ資産' && (
        <div
          className="flex items-center justify-center text-base text-bluegray-600 font-bold leading-6 border border-bluegray-400 rounded-lg h-10 mt-6 bg-gray-50 cursor-pointer"
          onClick={goToGrouping(pushRouting)(`${assetBundleType.key}`)}>
          グループを編集
        </div>
      )}
    </div>
  );
};
